import { type Ref, ref, computed } from "vue";
import axios from "axios";

interface ThemeSetting {
  typename?: string;
  key: string;
  value: string;
}

interface ColorEntry {
  key: string;
  value: string;
}

interface QuickShareData {
  title?: string;
  sessionToken?: string;
  colors?: ColorEntry[];
  customerDesign?: ColorEntry[];
  resellerDesign?: ColorEntry[];
  [key: string]: unknown;
}

export interface QuickShareApi {
  setTheme: (settings: ThemeSetting[]) => void;
}

export const useQuickshareComposable = (api: QuickShareApi) => {
  const quickshare: Ref<QuickShareData | null> = ref(null);
  const sessionCode: Ref<string | null> = ref(null);
  const quickShareKeyState: Ref<string | null> = ref(null);

  const loadQuickShareFromKey = async (key: string) => {
    try {
      const url = `${
        import.meta.env.VITE_APP_UCHI_API_ENDPOINT
      }/shares/${key}?include=colors,customerdesign,resellerdesign`;
      const config = {
        headers: {
          apikey: import.meta.env.VITE_APP_UCHI_API_KEY,
          "x-functions-key": import.meta.env.VITE_APP_UCHI_FUNCTION_KEY
        }
      };
      const response = await axios.get(url, config);
      quickshare.value = response.data;
      quickShareKeyState.value = key;
      sessionCode.value = response.data.sessionToken;

      if (response.data.colors) {
        api.setTheme(
          response.data.colors.map((c: ColorEntry) => ({
            typename: "colors",
            key: c.key.toLowerCase(),
            value: c.value
          }))
        );
      }
      if (response.data.customerDesign) {
        api.setTheme(
          response.data.customerDesign.map((c: ColorEntry) => ({
            typename: "customerdesign",
            key: c.key.toLowerCase(),
            value: c.value
          }))
        );
      }
      if (response.data.resellerDesign) {
        api.setTheme(
          response.data.resellerDesign.map((c: ColorEntry) => ({
            typename: "resellerdesign",
            key: c.key.toLowerCase(),
            value: c.value
          }))
        );
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: string } };
      if (error.response && error.response.data === "Share has expired") {
        throw new Error();
      }
    }
  };

  const validatePasswordOnQuickShare = async (key: string, password: string) => {
    const url = `${import.meta.env.VITE_APP_UCHI_API_ENDPOINT}/shares/${key}/verify`;
    const body = { code: password };
    const config = {
      headers: {
        apikey: import.meta.env.VITE_APP_UCHI_API_KEY,
        "x-functions-key": import.meta.env.VITE_APP_UCHI_FUNCTION_KEY
      }
    };
    try {
      const response = await axios.post(url, body, config);

      if (response.data.sessionToken) {
        sessionCode.value = response.data.sessionToken;
        return sessionCode.value;
      }
    } catch {
      return null;
    }
  };

  const resendPasswordOnQuickShare = async (externId: string) => {
    const url = `${import.meta.env.VITE_APP_UCHI_API_ENDPOINT}/shares/${externId}/resend`;
    const config = {
      headers: {
        apikey: import.meta.env.VITE_APP_UCHI_API_KEY,
        "x-functions-key": import.meta.env.VITE_APP_UCHI_FUNCTION_KEY
      }
    };
    const response = await axios.get(url, config);
    return response.data;
  };

  // Getters
  const quickShareTitle = computed(() => quickshare.value?.title);
  const quickShareSessionToken = computed(() => sessionCode.value);
  const quickShareKey = computed(() => quickShareKeyState.value);

  return {
    quickshare,
    sessionCode,
    quickShareKeyState,
    loadQuickShareFromKey,
    validatePasswordOnQuickShare,
    resendPasswordOnQuickShare,
    quickShareTitle,
    quickShareSessionToken,
    quickShareKey
  };
};
